/* eslint-disable react/forbid-prop-types */
/* eslint-disable react/destructuring-assignment */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/jsx-no-bind */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { unlistedSharesTransaction } from '@monestry-dev/schema';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { nanoid } from 'nanoid';
import { InformationCircleIcon } from '@heroicons/react/20/solid';
import { globalAccountsView } from '../../redux/reducers/globalSelectors';
import ButtonBar from './ButtonBar';
import getButtonsToShow from './getButtonsToShow';
import InputField from '../../elements/InputField';
import Button from '../../elements/Button';
import MiniSpinner from '../../misc/MiniSpinner';
import { postData } from '../../redux/reducers/data';
import { setMessage } from '../../redux/actions/message';

dayjs.extend(utc);

export default function UnlistedCapitalTransactionWizard(props) {
  const { account, setAccount, handleClick } = props;

  const { t } = useTranslation('accountDetails', { keyPrefix: 'unlistedShares.wizard.transactions' });
  const { t: tButtons } = useTranslation('accountDetails', { keyPrefix: 'buttons' });
  const { register, watch, formState, handleSubmit, reset, getValues } = useForm({ defaultValues: {}, mode: 'onChange' });

  const [loading, setLoading] = useState(false);
  const [loadingGoToAccount, setLoadingloadingGoToAccount] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [newTransactionDraft, setNewTransactionDraft] = useState({});

  const selectAccounts = useSelector(globalAccountsView);
  const connectedDepositAccountObjects = selectAccounts.filter((a) => account?.connectedDepositAccounts?.includes(a.id));
  // account disappears when we execute goToAccount, so we need to capture that

  const selectTransactions = useSelector((state) => state.data?.unlistedShares?.transactions || []);
  const accountTransactions = useMemo(
    () => (selectTransactions || [])
      .filter((txn) => txn.accountId === account.id)
      .filter((txn) => !txn.isSimulated)
      .sort((a, b) => a.date - b.date)
      .concat([{}]),
    [selectTransactions, account.id],
  );

  // null means "new transaction" placeholder (last item)
  const [selectedTransactionId, setSelectedTransactionId] = useState(null);
  const transactionIndex = useMemo(() => {
    if (!selectedTransactionId) return accountTransactions.length - 1;
    const foundIndex = accountTransactions.findIndex((txn) => txn.id === selectedTransactionId);
    return foundIndex >= 0 ? foundIndex : accountTransactions.length - 1;
  }, [selectedTransactionId, accountTransactions]);

  const dispatch = useDispatch();

  const watchTransactionType = watch('transactionType');

  const isNewTransaction = transactionIndex === accountTransactions.length - 1 || !accountTransactions[transactionIndex]?.id;

  function resetAndSetIndex(newIndex) {
    const txn = accountTransactions[newIndex];
    setSelectedTransactionId(txn?.id || null);
    if (!txn?.id) {
      reset(newTransactionDraft || {});
      return;
    }

    let txnTransactionType;
    if (txn.quantity === null || txn.quantity === undefined) txnTransactionType = undefined;
    else if (Number(txn.quantity) < 0) txnTransactionType = 'sharesSale';
    else txnTransactionType = 'moreShares';
    const txnUpac = txn.upac ?? txn.uptc ?? txn.upbc ?? undefined;
    reset({
      transactionType: txnTransactionType,
      dateUserInput: txn.date ? dayjs.utc(txn.date).format('YYYY-MM-DD') : undefined,
      quantity: txn.quantity === null || txn.quantity === undefined ? undefined : Math.abs(Number(txn.quantity)),
      upac: txnUpac === null || txnUpac === undefined ? undefined : Number(txnUpac),
    });
  }

  function handlePrevNext(direction) {
    const newIndex = direction === 'next' ? transactionIndex + 1 : transactionIndex - 1;
    if (newIndex < 0 || newIndex >= accountTransactions.length) return;

    if (formState.isDirty) {
      window.dispatchEvent(
        new CustomEvent('setAlert', {
          detail: {
            id: 'aboutToLoseData',
            caller: 'UnlistedCapitalTransactionWizard',
            callbackOk: () => resetAndSetIndex(newIndex),
          },
        }),
      );
      return;
    }

    if (isNewTransaction) setNewTransactionDraft(getValues());
    resetAndSetIndex(newIndex);
  }

  // If the selected transaction disappears (e.g., deleted elsewhere), fall back to "new" placeholder.
  useEffect(() => {
    if (selectedTransactionId && !accountTransactions.some((txn) => txn.id === selectedTransactionId)) {
      setSelectedTransactionId(null);
      reset({});
    }
  }, [accountTransactions, selectedTransactionId, reset]);

  function goToAccount(e, a) {
    setLoadingloadingGoToAccount(a.id);
    setAccount(a); // setAccount requires the entire account object
    handleClick('table');
    handleClick('holdings');
  }

  async function onSubmit(data) {
    setLoading(true);
    try {
      // moreCapital and capitalDisbursement require a deposit transaction (the user has been redirected there)
      // for moreShares and sharesSale, create a unlistedShares transaction
      const { transactionType, dateUserInput, upac, quantity } = data;

      const currentTxn = accountTransactions[transactionIndex];
      const base = !isNewTransaction && currentTxn?.id ? currentTxn : unlistedSharesTransaction;

      const transaction = {
        ...base,
        id: !isNewTransaction && currentTxn?.id ? currentTxn.id : nanoid(),
        accountId: account.id,
        assetId: account.id,
        date: dayjs.utc(dateUserInput).valueOf(),
        quantity: transactionType === 'moreShares' ? Number(quantity) : -Number(quantity), // set correct sign
        upac: Number(upac),
        accountCurrency: account.currency,
        assetCurrency: account.currency,
        upbc: null, // let backend convert it
        uptc: null,
        transactionCurrency: null,
        isSimulated: false,
        importFlag: isNewTransaction ? 'post' : 'put',
      };
      await dispatch(
        postData({
          data: [{ ...transaction }],
          category: 'unlistedShares',
          accountId: account.id,
        }),
      );
      // go back to table
      handleClick('table');
    } catch (error) {
      // handle errors
      dispatch(setMessage('dataUpdateError'));
    }
    setLoading(false);
  }

  function handleDelete() {
    if (deleting) return;
    setDeleting(true);

    // If this is a saved transaction, delete it; if this is the "new" entry, clear it when dirty.
    if (!isNewTransaction || formState.isDirty) {
      window.dispatchEvent(
        new CustomEvent('setAlert', {
          detail: {
            id: 'youAreAboutToDeleteTransaction',
            caller: 'UnlistedCapitalTransactionWizard',
            callbackOk: async () => {
              try {
                if (!isNewTransaction) {
                  const txn = accountTransactions[transactionIndex];
                  await dispatch(postData({ data: [{ ...txn, importFlag: 'delete' }], category: 'unlistedShares', accountId: account.id }));
                  handleClick('table');
                } else {
                  setNewTransactionDraft({});
                  resetAndSetIndex(accountTransactions.length - 1);
                }
              } catch (err) {
                console.error(err);
              } finally {
                setDeleting(false);
              }
            },
            callbackCancel: () => setDeleting(false),
          },
        }),
      );
      return;
    }

    setDeleting(false);
  }

  return (
    <div className="w-full h-full">
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <ButtonBar {...props} buttonsToShow={getButtonsToShow('unlistedShares', 'wizard', 'holdings')} handlePrevNext={handlePrevNext} />
      <form className="w-full h-full grid lg:grid-cols-2 2xl:grid-cols-3 py-8 px-2" onSubmit={handleSubmit(onSubmit)}>
        {/* COL 1 */}
        <div className="space-y-8" name="account-details-unlisted-capital-option">
          <h2 className="font-bold text-base sm:text-lg">{t('header')}</h2>
          <div className="space-y-4">
            <div className="flex space-x-2 items-center">
              <input
                id="moreShares"
                name="moreShares"
                type="radio"
                value="moreShares"
                className="h-4 w-4 border-gray-300 text-brandBlue-500 focus:ring-brandBlue-400"
                {...register('transactionType', { required: { value: true, message: t('error') } })}
              />
              <label htmlFor="moreShares" className="ml-3 block text-sm font-medium text-gray-700">
                {t('moreShares')}
              </label>
            </div>
            <div className="flex space-x-2 items-center">
              <input
                id="moreCapital"
                name="moreCapital"
                type="radio"
                value="moreCapital"
                className="h-4 w-4 border-gray-300 text-brandBlue-500 focus:ring-brandBlue-400"
                {...register('transactionType', { required: { value: true, message: t('error') } })}
              />
              <label htmlFor="moreCapital" className="ml-3 block text-sm font-medium text-gray-700">
                {t('moreCapital')}
              </label>
            </div>
            <div className="flex space-x-2 items-center">
              <input
                id="capitalDisbursement"
                name="capitalDisbursement"
                type="radio"
                value="capitalDisbursement"
                className="h-4 w-4 border-gray-300 text-brandBlue-500 focus:ring-brandBlue-400"
                {...register('transactionType', { required: { value: true, message: t('error') } })}
              />
              <label htmlFor="capitalDisbursement" className="ml-3 block text-sm font-medium text-gray-700">
                {t('capitalDisbursement')}
              </label>
            </div>
            <div className="flex space-x-2 items-center">
              <input
                id="sharesSale"
                name="sharesSale"
                type="radio"
                value="sharesSale"
                className="h-4 w-4 border-gray-300 text-brandBlue-500 focus:ring-brandBlue-400"
                {...register('transactionType', { required: { value: true, message: t('error') } })}
              />
              <label htmlFor="sharesSale" className="ml-3 block text-sm font-medium text-gray-700">
                {t('sharesSale')}
              </label>
            </div>
            <p className="mt-2 text-sm text-red-500" id="connectedDepositMode-error">
              {formState.errors.transactionType && <span>{formState.errors.transactionType.message}</span>}
            </p>
          </div>
        </div>
        {/* COL 2 (OR BELOW FOR SM-) */}
        {watchTransactionType ? (
          <div className="space-y-8 flex flex-col" name="account-details-unlisted-capital-form">
            <h2 className="font-bold text-base sm:text-lg">{t('formHeader')}</h2>
            {(watchTransactionType === 'moreShares' || watchTransactionType === 'sharesSale') && (
              <>
                <InputField
                  formatting="w-1/2"
                  id="dateUserInput"
                  type="date"
                  autoComplete="off"
                  label={t('dateUserInput.label')}
                  toolTip={t('dateUserInput.tooltip')}
                  placeholder="2"
                  register={register}
                  validationRules={{ required: { value: true, message: t('dateUserInput.error') } }}
                  errors={formState.errors}
                  optional={false}
                />
                <InputField
                  formatting="w-1/2"
                  id="quantity"
                  type="number"
                  autoComplete="off"
                  label={t('quantity.label')}
                  toolTip={t('quantity.tooltip')}
                  placeholder="2"
                  register={register}
                  validationRules={{ required: { value: true, message: t('quantity.error') } }}
                  errors={formState.errors}
                  optional={false}
                />
                <InputField
                  formatting="w-1/2"
                  id="upac"
                  type="number"
                  autoComplete="off"
                  label={t('upac.label')}
                  toolTip={t('upac.tooltip')}
                  placeholder="2"
                  register={register}
                  validationRules={{ required: { value: true, message: t('upac.error') } }}
                  errors={formState.errors}
                  currency={account.currency}
                  optional={false}
                />
              </>
            )}
            {watchTransactionType === 'moreCapital' || watchTransactionType === 'capitalDisbursement' ? (
              <div className="">
                <p className="col-span-3 prose text-sm text-gray-500">{t('clickToOpenDeposit')}</p>
                <div className="flex space-x-4 mt-8">
                  {connectedDepositAccountObjects.length === 0 && (
                    <>
                      <InformationCircleIcon className="w-5 h-5 text-brandBlue-300" />
                      <div className="text-gray-500 italic text-sm">{t('noConnectedDeposit')}</div>
                    </>
                  )}
                  {connectedDepositAccountObjects.map((a) => (
                    <button key={a.id} className="p-4 bg-white border border-gray-300 rounded-lg shadow-sm text-center hover:bg-gray-50" type="button" onClick={(e) => goToAccount(e, a)}>
                      {loadingGoToAccount === a.id ? (
                        <MiniSpinner className="w-5 h-5 text-gray-400 animate-spin" />
                      ) : (
                        <div className="text-gray-500 font-bold text-sm truncate uppercase">{a.name}</div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div />
            )}
          </div>
        ) : (
          <div />
        )}
        <div id="wizard-nav-bar" className="col-start-1 lg:col-span-2">
          <div className="flex space-x-8 justify-center">
            <Button text={tButtons('previous.label')} enabled={transactionIndex !== 0} onClick={() => handlePrevNext('previous')} toolTip={tButtons('previous.tooltip')} id="button-previous" />
            <div className="flex space-x-2">
              <Button formatting="col-start-3" text={t('submit')} type="submit" withAccent size="lg" spinnerOn={loading} enabled={formState.isValid && formState.isDirty && !deleting} />
              <Button text={t('deleteThisTransaction')} type="button" size="lg" onClick={handleDelete} enabled={!loading && (formState.isDirty || !isNewTransaction)} spinnerOn={deleting} />
            </div>
            <Button
              text={tButtons('next.label')}
              enabled={transactionIndex < accountTransactions.length - 1}
              onClick={() => handlePrevNext('next')}
              toolTip={tButtons('next.tooltip')}
              id="button-next"
            />
          </div>
        </div>
      </form>
    </div>
  );
}
UnlistedCapitalTransactionWizard.propTypes = {
  account: PropTypes.objectOf(PropTypes.any).isRequired,
  setAccount: PropTypes.func.isRequired,
  handleClick: PropTypes.func.isRequired,
};
